import React, { useState, useEffect, useRef } from "react";
import apiService from "services/apiService";
import GenericModal from "components/Modal";
import GenericToaster from "components/Toaster";
import { FaRegTrashAlt } from "react-icons/fa";

interface Release {
    id: number;
    platform: string;
    version: string;
    storageKey: string;
    fileSizeBytes: number;
    checksumSha256: string;
    publishedAt: string;
    publishedBy: string;
    latest: boolean;
    downloadUrl: string;
}

// Utility to format bytes into human-readable strings
const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Web Crypto API utility to calculate SHA-256 checksum
const calculateSHA256 = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    return hashHex;
};

const LiriAdminBuilds: React.FC = () => {
    const [builds, setBuilds] = useState<Release[]>([]);
    const [loading, setLoading] = useState(false);

    // Toast State
    const [toastData, setToastData] = useState<{
        message: string;
        severity: "info" | "error";
    } | null>(null);

    // Upload Modal State
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [uploadState, setUploadState] = useState<"idle" | "hashing" | "uploading" | "publishing">("idle");
    const [uploadError, setUploadError] = useState<string | null>(null);

    // Delete Modal State
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [releaseToDelete, setReleaseToDelete] = useState<Release | null>(null);
    const [deleting, setDeleting] = useState(false);

    // Form State
    const [platform, setPlatform] = useState("WINDOWS");
    const [version, setVersion] = useState("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const fetchBuilds = async () => {
        setLoading(true);
        try {
            const res = await apiService.get("api/app-updates/releases");
            setBuilds(res.data || []);
        } catch (error) {
            console.error("Failed to fetch builds", error);
            setToastData({
                message: "Failed to fetch releases",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBuilds();
    }, []);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setSelectedFile(e.target.files[0]);
            setUploadError(null);
        }
    };

    const resetForm = () => {
        setPlatform("WINDOWS");
        setVersion("");
        setSelectedFile(null);
        setUploadState("idle");
        setUploadError(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleUploadSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedFile || !version.trim() || !platform) {
            setUploadError("Please fill out all fields and select a file.");
            return;
        }

        try {
            setUploadError(null);

            // 1. Calculate Checksum
            setUploadState("hashing");
            const checksumSha256 = await calculateSHA256(selectedFile);

            // 2. Request Pre-signed Upload URL
            setUploadState("uploading");
            const urlRes = await apiService.post("api/app-updates/upload-url", {
                platform,
                version: version.trim(),
                fileName: selectedFile.name,
            });
            const { uploadUrl, key } = urlRes.data;

            if (!uploadUrl || !key) {
                throw new Error("Failed to retrieve upload URL from server.");
            }

            // 3. PUT raw file directly to Storage URL (using native fetch to avoid auth headers)
            const putRes = await fetch(uploadUrl, {
                method: "PUT",
                body: selectedFile,
                headers: {
                    "Content-Type": selectedFile.type || "application/octet-stream",
                },
            });

            if (!putRes.ok) {
                throw new Error(`Storage upload failed with status ${putRes.status}`);
            }

            // 4. Publish the Release
            setUploadState("publishing");
            await apiService.post("api/app-updates/publish", {
                platform,
                version: version.trim(),
                key,
                checksumSha256,
            });

            // 5. Cleanup & Refresh
            setIsUploadModalOpen(false);
            resetForm();
            fetchBuilds();
            setToastData({
                message: "Release published successfully",
                severity: "info",
            });
            //@disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: unknown) {
            console.error("Upload process failed:", error);

            let errorMessage = "An error occurred during upload.";

            if (error instanceof Error) {
                errorMessage = error.message;

                if ('response' in error && typeof error.response === 'object' && error.response !== null) {
                    const response = error.response as { data?: { message?: string } };
                    if (response.data?.message) {
                        errorMessage = response.data.message;
                    }
                }
            } else if (typeof error === 'string') {
                errorMessage = error;
            }

            setUploadError(errorMessage);
            setUploadState("idle");
        }
    };

    const openUploadModal = () => {
        resetForm();
        setIsUploadModalOpen(true);
    };

    const handleOpenDelete = (release: Release) => {
        setReleaseToDelete(release);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!releaseToDelete || deleting) return;
        setDeleting(true);
        try {
            await apiService.delete(`api/app-updates/releases/${releaseToDelete.id}`);
            setIsDeleteModalOpen(false);
            setReleaseToDelete(null);
            fetchBuilds();
            setToastData({
                message: "Release deleted successfully",
                severity: "info",
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            console.error("Failed to delete release", error);
            // Grab the specific string response from the server error
            const msg = error?.response?.data || "Failed to delete release";
            setIsDeleteModalOpen(false);
            setReleaseToDelete(null);
            setToastData({
                message: msg,
                severity: "error",
            });
        } finally {
            setDeleting(false);
        }
    };

    // Helper for dynamic file types
    const getAcceptedFileTypes = (currentPlatform: string) => {
        switch (currentPlatform) {
            case "WINDOWS": return ".exe,.msi";
            case "MAC": return ".dmg,.app";
            case "LINUX": return ".deb,.rpm,.AppImage";
            default: return ".exe,.msi,.dmg,.pkg,.app,.AppImage,.deb,.rpm";
        }
    };

    return (
        <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl border border-slate-100 dark:border-slate-700 flex flex-col overflow-hidden shadow-sm">

            {/* Header matching the ConfigTable style */}
            <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Configuration</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Manage liri admin application releases</p>
                </div>

                {/* Trigger Modal Button */}
                <button
                    onClick={openUploadModal}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    <span>Upload Build</span>
                </button>
            </div>

            {/* Table section */}
            <div className="flex-1 overflow-auto">
                <table className="w-full text-left text-sm text-slate-600 dark:text-slate-400">
                    <thead className="bg-slate-50 dark:bg-gray-900/50 text-slate-900 dark:text-slate-200 uppercase text-xs font-bold sticky top-0 border-b border-slate-100 dark:border-slate-700">
                        <tr>
                            <th className="px-6 py-4">NAME & PLATFORM</th>
                            <th className="px-6 py-4">STATUS / VERSION</th>
                            <th className="px-6 py-4">DETAILS</th>
                            <th className="px-6 py-4 text-right">ACTION</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {loading ? (
                            <tr>
                                <td colSpan={4} className="px-6 py-8 text-center text-slate-500">Loading releases...</td>
                            </tr>
                        ) : builds.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="px-6 py-8 text-center text-slate-500">No releases published yet.</td>
                            </tr>
                        ) : (
                            builds.map((build) => {
                                const fileName = build.storageKey.split("/").pop() || "Unknown File";
                                return (
                                    <tr key={build.id} className="hover:bg-slate-50 dark:hover:bg-gray-800/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-slate-900 dark:text-white">{fileName}</div>
                                            <div className="text-xs text-slate-500 mt-0.5 uppercase tracking-wide">{build.platform}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-slate-800 dark:text-slate-200">v{build.version}</span>
                                                {build.latest && (
                                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                                        LATEST
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm">{formatBytes(build.fileSizeBytes)}</div>
                                            <div className="text-xs text-slate-500 mt-0.5">
                                                {new Date(build.publishedAt).toLocaleDateString(undefined, {
                                                    year: 'numeric', month: 'short', day: 'numeric'
                                                })}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-3">
                                                <a
                                                    href={build.downloadUrl}
                                                    target="_blank"
                                                    rel="norenoopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium inline-flex items-center gap-1"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                    </svg>
                                                    Download
                                                </a>
                                                <button
                                                    onClick={() => handleOpenDelete(build)}
                                                    className="btn-red px-2 py-2 rounded-lg"
                                                >
                                                    <FaRegTrashAlt size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Upload Modal */}
            {isUploadModalOpen && (
                <GenericModal
                    isOpen={isUploadModalOpen}
                    onClose={() => {
                        if (uploadState === "idle") setIsUploadModalOpen(false);
                    }}
                    header="Publish New Release"
                    size="md"
                >
                    <form onSubmit={handleUploadSubmit} className="space-y-5 px-1 py-2">

                        {uploadError && (
                            <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg border border-red-100">
                                {uploadError}
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Platform <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={platform}
                                onChange={(e) => setPlatform(e.target.value)}
                                disabled={uploadState !== "idle"}
                                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                            >
                                <option value="WINDOWS">Windows</option>
                                <option value="MAC">macOS</option>
                                <option value="LINUX">Linux</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Version <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={version}
                                onChange={(e) => setVersion(e.target.value)}
                                placeholder="e.g., 1.0.4"
                                disabled={uploadState !== "idle"}
                                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Build File <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                disabled={uploadState !== "idle"}
                                accept={getAcceptedFileTypes(platform)}
                                className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 dark:text-slate-400 dark:file:bg-slate-700 dark:file:text-slate-200"
                            />
                            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                                Supported formats for {platform === "WINDOWS" ? "Windows" : platform === "MAC" ? "macOS" : "Linux"}:{" "}
                                <span className="font-medium text-slate-700 dark:text-slate-300">
                                    {getAcceptedFileTypes(platform).replace(/,/g, ", ")}
                                </span>
                            </p>
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-700">
                            <button
                                type="button"
                                onClick={() => setIsUploadModalOpen(false)}
                                disabled={uploadState !== "idle"}
                                className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={uploadState !== "idle" || !selectedFile || !version.trim()}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 min-w-[120px] justify-center"
                            >
                                {uploadState === "idle" && "Publish Release"}
                                {uploadState === "hashing" && "Calculating hash..."}
                                {uploadState === "uploading" && "Uploading..."}
                                {uploadState === "publishing" && "Publishing..."}
                            </button>
                        </div>

                    </form>
                </GenericModal>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <GenericModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    header="Delete Release"
                    size="md"
                >
                    <div className="px-1 py-2">
                        <p className="text-base text-slate-600 dark:text-slate-300 mb-6">
                            Are you sure you want to delete this release?
                        </p>
                        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-700">
                            <button
                                type="button"
                                onClick={() => setIsDeleteModalOpen(false)}
                                disabled={deleting}
                                className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmDelete}
                                disabled={deleting}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 min-w-[100px]"
                            >
                                {deleting ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </GenericModal>
            )}

            {/* Toast */}
            {toastData && (
                <GenericToaster
                    open={true}
                    message={toastData.message}
                    severity={toastData.severity}
                    onClose={() => setToastData(null)}
                />
            )}
        </div>
    );
};

export default LiriAdminBuilds;